import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes, Grade, Icon, IconNames, JournalPreloader } from 'atoms';
import { parseDate } from 'services/DateService';
import { HomeworkAssignmentCard } from 'organisms/HomeworkSubmit';

import type { ITextbookFile, IHomeworkAssignment, IHomeworkRosterRow } from 'core';

import { HomeworkAssignmentForm } from './HomeworkAssignmentForm';
import type { IHomeworkAssignmentFormValues } from './HomeworkAssignmentForm';

/** Пустышка для карточки, когда задания ещё нет (ТЗ 0026). */
const EMPTY_ASSIGNMENT: IHomeworkAssignment = {
  id: '',
  lessonId: '',
  hasTeacherAssignment: false,
  title: '',
  text: '',
  textbookRef: '',
  dueDate: null,
  updatedAt: null,
};

export interface IHomeworkCheck {
  assignment: IHomeworkAssignment | null;
  /** ТЗ 0026: учебник занятия — преподаватель видит то же, что студент. */
  textbookFiles?: ITextbookFile[];
  assignmentError: string;
  onRetryAssignment: () => void;
  editing: boolean;
  saveLoading: boolean;
  roster: IHomeworkRosterRow[];
  rosterLoading: boolean;
  rosterError: string;
  bulkCount: number;
  bulkProgress: { done: number; total: number } | null;
  onBulkGradeDone: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveAssignment: (values: IHomeworkAssignmentFormValues) => Promise<boolean>;
  onOpenStudent: (studentId: string) => void;
}

/**
 * Teacher's homework view (spec 0012 §6): assignment block on top
 * (create/edit), then the roster — one row per student of the lesson with
 * submitted/not-submitted state, current hw_mark and the AI verdict summary.
 * A row click opens HomeworkReviewDrawer; grading happens ONLY there
 * (the legacy setStudentsMarks grid stays for coursework/graduation only).
 *
 * The roster renders WHETHER OR NOT the lesson has an assignment: grading a
 * lesson whose homework was handed out off-platform is exactly what the old
 * grid did, and the backend still serves it.
 */
export const HomeworkCheckComponent = memo(
  ({
    assignment,
    textbookFiles = [],
    assignmentError,
    onRetryAssignment,
    editing,
    saveLoading,
    roster,
    rosterLoading,
    rosterError,
    bulkCount,
    bulkProgress,
    onBulkGradeDone,
    onStartEdit,
    onCancelEdit,
    onSaveAssignment,
    onOpenStudent,
  }: IHomeworkCheck) => {
    const { t } = useTranslation();

    return (
      <Wrapper>
        <HeaderWrapper>
          <Title>{t('homework.assignment.title')}</Title>
          {assignment && !editing && (
            <EditButton
              variant="outline"
              size={ButtonSizes.sm}
              colors={{ borderColor: Colors.gray }}
              sidePaddings={12}
              onClick={onStartEdit}>
              {t('homework.assignment.edit')}
            </EditButton>
          )}
        </HeaderWrapper>

        {editing ? (
          <HomeworkAssignmentForm
            assignment={assignment}
            saveLoading={saveLoading}
            onSave={onSaveAssignment}
            onCancel={onCancelEdit}
          />
        ) : assignmentError ? (
          // MUST come before the create-CTA: a failed query also yields a null
          // assignment, and «нет задания» would invite the teacher to create a
          // second one on top of the real one.
          <ErrorPanel role="alert">
            <EmptyText>{t('homework.assignment.loadError')}</EmptyText>
            <RetryButton
              variant="outline"
              size={ButtonSizes.sm}
              colors={{ borderColor: Colors.gray }}
              sidePaddings={12}
              onClick={onRetryAssignment}>
              {t('homework.retry')}
            </RetryButton>
          </ErrorPanel>
        ) : assignment ? (
          <HomeworkAssignmentCard assignment={assignment} textbookFiles={textbookFiles} />
        ) : (
          <>
            {/* ТЗ 0026: даже без задания преподаватель обязан видеть учебник —
                иначе он не знает, что именно проверять, а это самый частый
                случай на проде. */}
            <HomeworkAssignmentCard assignment={EMPTY_ASSIGNMENT} textbookFiles={textbookFiles} />
            <CreateButton onClick={onStartEdit}>{t('homework.assignment.create')}</CreateButton>
          </>
        )}

        <RosterWrapper>
          <RosterHeader>
            <Title>{t('homework.roster.title')}</Title>
            {bulkCount >= 2 && (
              <BulkButton
                variant="outline"
                size={ButtonSizes.sm}
                colors={{ borderColor: Colors.gray }}
                sidePaddings={12}
                disabled={Boolean(bulkProgress)}
                onClick={onBulkGradeDone}>
                {bulkProgress
                  ? t('homework.roster.bulkProgress', {
                      done: bulkProgress.done,
                      total: bulkProgress.total,
                    })
                  : t('homework.roster.bulkDone', { n: bulkCount })}
              </BulkButton>
            )}
          </RosterHeader>
          {/* A failed poll must not blank a roster we already have. */}
          {rosterError && roster.length > 0 && (
            <InlineWarning role="status">{t('homework.roster.refreshFailed')}</InlineWarning>
          )}
          {rosterLoading && roster.length === 0 ? (
            <JournalPreloader />
          ) : rosterError && roster.length === 0 ? (
            <EmptyText>{t('homework.loadError')}</EmptyText>
          ) : roster.length === 0 ? (
            <EmptyText>{t('homework.roster.empty')}</EmptyText>
          ) : (
            <RosterList>
              {roster.map((row) => (
                <RosterRow key={row.studentId} onClick={() => onOpenStudent(row.studentId)}>
                  <StudentName>{row.studentName}</StudentName>
                  <RowMeta>
                    {row.submission ? (
                      <SubmittedChip $submitted>
                        {t('homework.status.submitted')} (
                        {[
                          row.submission.hasGithub && t('homework.submit.kindGithub'),
                          row.submission.hasFile && t('homework.submit.kindZip'),
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        , {parseDate(row.submission.submittedAt)})
                      </SubmittedChip>
                    ) : (
                      <SubmittedChip>{t('homework.status.notSubmitted')}</SubmittedChip>
                    )}
                    {row.hwMark && <Grade gradeType={row.hwMark} />}
                    {row.submission?.aiReview?.isDone && row.submission.aiReview.aiScore !== null && (
                      <AiScore>
                        {t('homework.roster.aiScore', {
                          score: row.submission.aiReview.aiScore,
                        })}
                      </AiScore>
                    )}
                    {row.submission?.aiReview?.suspiciousContent && (
                      <IconWrapper title={`${t('homework.roster.suspicious')}`}>
                        <Icon name={IconNames.WarningYellow} width={16} height={16} />
                      </IconWrapper>
                    )}
                  </RowMeta>
                </RosterRow>
              ))}
            </RosterList>
          )}
        </RosterWrapper>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  ${desktop} {
    width: 700px;
  }
`;

const HeaderWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${tablet} {
    flex-direction: row;
    gap: 12px;
    align-items: center;
  }
`;

const Title = styled.h3`
  margin: 0;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const EditButton = styled(Button)`
  width: max-content;
`;

const CreateButton = styled(Button)`
  margin-top: 16px;
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${Colors.gray};
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const RosterWrapper = styled.div`
  margin-top: 32px;
`;

const RosterHeader = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const BulkButton = styled(Button)`
  width: max-content;
`;

const RetryButton = styled(Button)`
  width: max-content;
  margin-top: 12px;
`;

const ErrorPanel = styled.div`
  padding: 12px;
  border: 1px solid ${Colors.additional_gray};
  border-radius: 4px;
`;

const InlineWarning = styled.p`
  margin: 0 0 12px;
  color: ${Colors.gray};
  ${fontService.text({ size: 12, lineHeight: 150, weight: FontWeight.regular })};
`;

const RosterList = styled.div`
  width: 100%;
`;

const RosterRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid ${Colors.additional_gray};
  cursor: pointer;

  &:last-child {
    border-bottom: 1px solid ${Colors.additional_gray};
  }

  &:hover {
    background-color: ${Colors.bg_gray};
  }

  ${tablet} {
    flex-direction: row;
    align-items: center;
  }
`;

const StudentName = styled.p`
  margin: 0;
  ${fontService.text({ size: 14, weight: FontWeight.regular, lineHeight: 142 })};
`;

const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SubmittedChip = styled.span<{ $submitted?: boolean }>`
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${Colors.bg_gray};
  color: ${({ $submitted }) => ($submitted ? Colors.black : Colors.gray)};
  ${fontService.text({ size: 12, lineHeight: 150, weight: FontWeight.regular })};
`;

const AiScore = styled.span`
  ${fontService.text({ size: 12, lineHeight: 150, weight: FontWeight.medium })};
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;
